import { useMemo, useState } from "react";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { RelativeTime } from "@/components/admin/RelativeTime";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AdminAgentInstance } from "@/queries/adminAgents";
import {
  useAdminAgents,
  useAgentInstanceAudit,
  useUpdateAgentInstance,
} from "@/queries/adminAgents";

import { formatConfigValue } from "./utils";

interface AgentInstanceDetailPageProps {
  instanceId: string;
}

export function AgentInstanceDetailPage({ instanceId }: AgentInstanceDetailPageProps) {
  const { t } = useTranslation("admin-agents");
  const navigate = useNavigate();
  const { data, isPending, isError } = useAdminAgents();

  if (isPending) {
    return <Shell><p className="text-sm text-muted-foreground">{t("page.loading")}</p></Shell>;
  }
  if (isError || !data) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const instance = data.instances.find((i) => i.instance_id === instanceId);
  const family = instance
    ? data.families.find((f) => f.skill === instance.skill)
    : undefined;

  if (!instance || !family) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertDescription>{t("page.loadError")}</AlertDescription>
        </Alert>
        <Button variant="ghost" onClick={() => void navigate({ to: "/admin/agents" })}>
          <ArrowLeft className="h-4 w-4" />
          {t("page.backToOverview")}
        </Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void navigate({ to: "/admin/agents" })}
        className="self-start"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("page.backToOverview")}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>
            <code className="font-mono">{instance.instance_id}</code>
          </CardTitle>
          <CardDescription>{family.skill}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Parameters instance={instance} />
          <OverridesEditor
            instance={instance}
            familyDefaults={family.defaults}
            allowedKeys={family.allowed_keys}
          />
        </CardContent>
      </Card>

      <AuditFeed instanceId={instance.instance_id} />
    </Shell>
  );
}

// ── Parameters (read-only) ──────────────────────────────────────────

function Parameters({ instance }: { instance: AdminAgentInstance }) {
  const { t } = useTranslation("admin-agents");
  const entries = Object.entries(instance.parameters);
  return (
    <section className="flex flex-col gap-2" data-testid="instance-parameters">
      <h2 className="text-sm font-semibold">{t("instance.parameters")}</h2>
      <p className="text-xs text-muted-foreground">
        {t("instance.parametersReadOnly")}
      </p>
      <div className="flex flex-wrap gap-2 text-xs">
        {entries.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          entries.map(([k, v]) => (
            <span
              key={k}
              className="rounded border border-dashed px-1.5 py-0.5 text-muted-foreground"
              data-testid={`parameter-${k}`}
            >
              <span className="font-medium">{k}</span>: {formatConfigValue(v)}
            </span>
          ))
        )}
      </div>
    </section>
  );
}

// ── Overrides editor ────────────────────────────────────────────────

interface OverridesEditorProps {
  instance: AdminAgentInstance;
  familyDefaults: Record<string, unknown>;
  allowedKeys: string[];
}

function OverridesEditor({
  instance,
  familyDefaults,
  allowedKeys,
}: OverridesEditorProps) {
  const { t } = useTranslation("admin-agents");
  const mutation = useUpdateAgentInstance(instance.instance_id);

  // Working copy of overrides: string-edited per field, undefined means
  // "fall back to family default".
  const [draft, setDraft] = useState<Record<string, string | undefined>>(() =>
    toStringEdits(instance.overrides),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isOverridden = useMemo(() => {
    const out = new Map<string, boolean>();
    for (const k of allowedKeys) out.set(k, draft[k] !== undefined);
    return out;
  }, [draft, allowedKeys]);

  const reset = (key: string) =>
    setDraft((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const onSave = () => {
    setSubmitError(null);
    const overrides = buildOverrides(draft, allowedKeys);
    mutation.mutate(
      { overrides },
      {
        onSuccess: () => {
          setDraft(toStringEdits(overrides));
        },
        onError: (err) => setSubmitError(err.message || t("errors.saveGeneric")),
      },
    );
  };

  return (
    <section className="flex flex-col gap-3" data-testid="instance-overrides">
      <h2 className="text-sm font-semibold">{t("instance.overrides")}</h2>
      {allowedKeys.map((key) => {
        const overridden = isOverridden.get(key) === true;
        const familyValue = familyDefaults[key];
        return (
          <div key={key} className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label htmlFor={`override-${instance.instance_id}-${key}`}>
                {t(`family.fieldLabels.${key}`, { defaultValue: key })}
                {overridden ? (
                  <Badge variant="outline" className="ml-2">
                    {t("instance.overrideBadge")}
                  </Badge>
                ) : null}
              </Label>
              {overridden ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => reset(key)}
                  data-testid={`override-reset-${key}`}
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("instance.resetToDefault")}
                </Button>
              ) : null}
            </div>
            <Input
              id={`override-${instance.instance_id}-${key}`}
              value={draft[key] ?? ""}
              placeholder={overridden ? "" : formatConfigValue(familyValue)}
              onChange={(e) =>
                setDraft((p) => ({ ...p, [key]: e.target.value }))
              }
              data-testid={`override-input-${key}`}
            />
            {!overridden ? (
              <p className="text-xs text-muted-foreground">
                {t("instance.fieldFallsBack", { value: formatConfigValue(familyValue) })}
              </p>
            ) : null}
          </div>
        );
      })}

      {submitError ? (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex gap-2">
        <Button onClick={onSave} disabled={mutation.isPending} data-testid="overrides-save">
          {mutation.isPending
            ? t("dialogs.instanceOverrides.saving")
            : t("dialogs.instanceOverrides.save")}
        </Button>
      </div>
    </section>
  );
}

// ── Audit feed ──────────────────────────────────────────────────────

function AuditFeed({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation("admin-agents");
  const { data, isPending, isError } = useAgentInstanceAudit(instanceId);

  return (
    <Card data-testid="instance-audit-feed">
      <CardHeader>
        <CardTitle>{t("instance.audit.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p className="text-sm text-muted-foreground">{t("page.loading")}</p>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertDescription>{t("instance.audit.loadError")}</AlertDescription>
          </Alert>
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("instance.audit.empty")}</p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm">
            {data.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-col gap-1 border-b pb-2 last:border-b-0"
              >
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <code>{entry.action}</code>
                  <span>·</span>
                  <RelativeTime
                    iso={entry.timestamp}
                    fallbackKey="instance.lastRunNever"
                    fallbackNs="admin-agents"
                  />
                  <span>·</span>
                  <span>
                    {t("instance.audit.by")}{" "}
                    {entry.actor_display_name ?? t("instance.audit.unknownActor")}
                  </span>
                </div>
                {Object.keys(entry.payload).length > 0 ? (
                  <pre className="overflow-x-auto rounded bg-muted px-2 py-1 text-xs">
                    {JSON.stringify(entry.payload, null, 2)}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function toStringEdits(
  overrides: Record<string, unknown>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(overrides)) {
    if (Array.isArray(v)) out[k] = v.join(",");
    else if (v !== null && v !== undefined) out[k] = String(v);
  }
  return out;
}

function buildOverrides(
  draft: Record<string, string | undefined>,
  allowedKeys: readonly string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of allowedKeys) {
    const v = draft[k];
    if (v === undefined || v === "") continue;
    if (k === "sources") {
      out[k] = v
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      continue;
    }
    if (k === "cadence_minutes") {
      const n = Number(v);
      out[k] = Number.isFinite(n) ? Math.trunc(n) : v;
      continue;
    }
    out[k] = v;
  }
  return out;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-6 p-6">{children}</div>;
}
